import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import LayoutPage from "../../Components/LayoutPage";
import TableLoading from "../../Components/Loader/TableLoading";
import SVG from "../../Components/SVG";
import ImageUploadPreviewGrid from "../../Components/UploadPreview/ImageUploadPreviewGrid";
import { useNotificationCounts } from "../../context/NotificationCountContext";
import Spinner from "../../Components/Loader/Spinner";
import { motion } from "framer-motion";
import { API_BASE_URL } from "../../config/api";


const ACCOUNT_GROUPS = ["live", "inactive", "passed", "failed"];
const KYC_FILE_ACCEPT = ".jpg,.jpeg,.png";
const COMPRESSIBLE_IMAGE_TYPES = new Set([
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/webp",
]);
const MAX_COMPRESS_DIMENSION = 1600;
const JPEG_COMPRESS_QUALITY = 0.78;
const CHAT_TEXTAREA_MIN_HEIGHT = 44;
const CHAT_TEXTAREA_MAX_HEIGHT = 112;
const PERSIAN_NUMBER_FORMATTER = new Intl.NumberFormat("fa-IR");

const sortByUpdatedAtDesc = (items = []) => {
  return [...items].sort((a, b) => {
    const aTime = new Date(a?.updated_at || 0).getTime();
    const bTime = new Date(b?.updated_at || 0).getTime();
    return bTime - aTime;
  });
};

const sortByCreatedAtAsc = (items = []) => {
  return [...items].sort((a, b) => {
    const aTime = new Date(a?.created_at || 0).getTime();
    const bTime = new Date(b?.created_at || 0).getTime();
    return aTime - bTime;
  });
};

const formatChatDateTime = (isoString) => {
  if (!isoString) return "";

  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return "";

  return new Intl.DateTimeFormat("fa-IR", {
    timeZone: "Asia/Tehran",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

const formatRelativeTimeAgo = (isoString) => {
  if (!isoString) return "";

  const date = new Date(isoString);
  const timestamp = date.getTime();
  if (Number.isNaN(timestamp)) return "";

  const diffSeconds = Math.max(0, Math.floor((Date.now() - timestamp) / 1000));
  if (diffSeconds < 60) return "لحظاتی قبل";

  const units = [
    { seconds: 365 * 24 * 60 * 60, label: "سال" },
    { seconds: 30 * 24 * 60 * 60, label: "ماه" },
    { seconds: 7 * 24 * 60 * 60, label: "هفته" },
    { seconds: 24 * 60 * 60, label: "روز" },
    { seconds: 60 * 60, label: "ساعت" },
    { seconds: 60, label: "دقیقه" },
  ];

  const unit = units.find((item) => diffSeconds >= item.seconds);
  const value = Math.floor(diffSeconds / unit.seconds);

  return `${PERSIAN_NUMBER_FORMATTER.format(value)} ${unit.label} پیش`;
};

const getErrorMessageFromPayload = (payload, fallbackText) => {
  if (!payload) return fallbackText;
  if (typeof payload === "string" && payload.trim()) {
    return payload;
  }
  if (typeof payload.detail === "string" && payload.detail.trim()) {
    return payload.detail;
  }

  const firstArrayValue = Object.values(payload).find(
    (value) => Array.isArray(value) && value.length > 0,
  );
  if (firstArrayValue && typeof firstArrayValue[0] === "string") {
    return firstArrayValue[0];
  }

  return fallbackText;
};

const flattenAccounts = (data) => {
  const allAccounts = [];
  ACCOUNT_GROUPS.forEach((groupKey) => {
    const list = Array.isArray(data?.[groupKey]) ? data[groupKey] : [];
    list.forEach((item) => allAccounts.push(item));
  });

  const uniqueByCode = new Map();
  allAccounts.forEach((account) => {
    const code = account?.code;
    if (code === undefined || code === null || code === "") return;
    uniqueByCode.set(String(code), account);
  });

  return Array.from(uniqueByCode.values()).sort((a, b) => {
    const aNum = Number(a?.code);
    const bNum = Number(b?.code);
    const bothNumeric = !Number.isNaN(aNum) && !Number.isNaN(bNum);
    if (bothNumeric) return bNum - aNum;
    return String(b?.code || "").localeCompare(String(a?.code || ""), "fa");
  });
};

const buildAccountLabel = (account) => {
  const accountCode = account?.code ? `(${account.code})` : "";
  const challengeType =
    account?.challenge_type === "real" ? "حساب واقعی" : "حساب تمرینی";
  const phaseType =
    account?.phase_type === "phase1"
      ? "فاز اول"
      : account?.phase_type === "phase2"
        ? "فاز دوم"
        : "فاندد";

  return `${challengeType} ${phaseType} ${accountCode}`.trim();
};

const canvasToBlob = (canvas, type, quality) => {
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), type, quality);
  });
};

const loadImageFromFile = (file) => {
  return new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      resolve({ img, objectUrl });
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error("image-load-failed"));
    };
    img.src = objectUrl;
  });
};

const compressImageFile = async (file) => {
  if (!COMPRESSIBLE_IMAGE_TYPES.has(String(file?.type || "").toLowerCase())) {
    return file;
  }

  try {
    const { img, objectUrl } = await loadImageFromFile(file);
    const width = Number(img.naturalWidth || img.width || 0);
    const height = Number(img.naturalHeight || img.height || 0);

    if (width <= 0 || height <= 0) {
      URL.revokeObjectURL(objectUrl);
      return file;
    }

    const largestSide = Math.max(width, height);
    const scale =
      largestSide > MAX_COMPRESS_DIMENSION
        ? MAX_COMPRESS_DIMENSION / largestSide
        : 1;

    const targetWidth = Math.max(1, Math.round(width * scale));
    const targetHeight = Math.max(1, Math.round(height * scale));

    const canvas = document.createElement("canvas");
    canvas.width = targetWidth;
    canvas.height = targetHeight;
    const context = canvas.getContext("2d");

    if (!context) {
      URL.revokeObjectURL(objectUrl);
      return file;
    }

    context.drawImage(img, 0, 0, targetWidth, targetHeight);
    URL.revokeObjectURL(objectUrl);

    const outputType =
      file.type === "image/png" || file.type === "image/webp"
        ? file.type
        : "image/jpeg";
    const quality =
      outputType === "image/png" ? undefined : JPEG_COMPRESS_QUALITY;

    const blob = await canvasToBlob(canvas, outputType, quality);
    if (!blob || blob.size >= file.size) {
      return file;
    }

    const baseName = file.name.replace(/\.[^/.]+$/, "");
    const nextExtension =
      outputType === "image/png"
        ? "png"
        : outputType === "image/webp"
          ? "webp"
          : "jpg";
    const nextName = `${baseName}.${nextExtension}`;

    return new File([blob], nextName, {
      type: outputType,
      lastModified: Date.now(),
    });
  } catch (error) {
    console.error("Image compression failed:", error);
    return file;
  }
};

const compressImageFiles = async (files = []) => {
  return Promise.all(files.map((file) => compressImageFile(file)));
};

const LoadingState = () => {
  return (
    <div className="w-full flex justify-center items-center gap-2 mt-5">
      <p className="text-[#808080]">در حال بارگذاری</p>
      <TableLoading className="text-[#808080]" />
    </div>
  );
};

const EmptyState = () => {
  return (
    <div className="w-full flex justify-center items-center flex-col gap-5 mt-5">
      <p className="text-zi-text-light dark:text-zi-text-dark">
        موردی برای نمایش وجود ندارد
      </p>
      <SVG name="noData" classname="w-24 fill-cyan-500" />
    </div>
  );
};

export default function Support() {
  const navigate = useNavigate();
  const { refetchNotificationCounts } = useNotificationCounts();

  const [ticketListRefreshSpinTick, setTicketListRefreshSpinTick] = useState(0);
  const [ticketDetailRefreshSpinTick, setTicketDetailRefreshSpinTick] =
    useState(0);

  const [priorityTickets, setPriorityTickets] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState("");

  const [selectedTicketUuid, setSelectedTicketUuid] = useState(null);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [accountsError, setAccountsError] = useState("");

  const [createTitle, setCreateTitle] = useState("");
  const [createDescription, setCreateDescription] = useState("");
  const [createAccountCode, setCreateAccountCode] = useState("");
  const [createDocuments, setCreateDocuments] = useState([]);
  const [createFormError, setCreateFormError] = useState("");
  const [createSubmitting, setCreateSubmitting] = useState(false);
  const [createUploadProgress, setCreateUploadProgress] = useState(null);

  const [chatText, setChatText] = useState("");
  const [chatDocuments, setChatDocuments] = useState([]);
  const [chatError, setChatError] = useState("");
  const [chatSubmitting, setChatSubmitting] = useState(false);
  const [chatUploadProgress, setChatUploadProgress] = useState(null);

  const markReadInProgressRef = useRef(new Set());
  const chatTextareaRef = useRef(null);
  const chatScrollContainerRef = useRef(null);
  const pendingInitialChatScrollTicketRef = useRef(null);

  const scrollChatToBottom = useCallback(() => {
    const container = chatScrollContainerRef.current;
    if (!container) return;
    container.scrollTop = container.scrollHeight;
  }, []);

  const handleChatDocumentLoad = useCallback(() => {
    if (pendingInitialChatScrollTicketRef.current !== selectedTicketUuid) return;
    scrollChatToBottom();
  }, [scrollChatToBottom, selectedTicketUuid]);

  const resetChatTextareaHeight = useCallback(() => {
    if (!chatTextareaRef.current) return;
    chatTextareaRef.current.style.height = `${CHAT_TEXTAREA_MIN_HEIGHT}px`;
  }, []);

  const resizeChatTextarea = useCallback((textarea) => {
    if (!textarea) return;
    if (!textarea.value) {
      textarea.style.height = `${CHAT_TEXTAREA_MIN_HEIGHT}px`;
      return;
    }

    textarea.style.height = "auto";
    textarea.style.height = `${Math.max(
      CHAT_TEXTAREA_MIN_HEIGHT,
      Math.min(textarea.scrollHeight, CHAT_TEXTAREA_MAX_HEIGHT),
    )}px`;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem("access");
    localStorage.removeItem("authCredentials");
    localStorage.removeItem("refresh");
    localStorage.removeItem("role");
    localStorage.removeItem("email");
    navigate("/signin");
  }, [navigate]);

  const refreshAccessToken = useCallback(async () => {
    const refreshResponse = await fetch(
      `${API_BASE_URL}auth/trader/jwt/refresh/`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          refresh: localStorage.getItem("refresh"),
        }),
      },
    );

    if (!refreshResponse.ok) {
      logout();
      return null;
    }

    const refreshData = await refreshResponse.json();
    localStorage.setItem("access", refreshData.access);
    return refreshData.access;
  }, [logout]);

  const authFetch = useCallback(
    async (url, options = {}) => {
      const headers = new Headers(options.headers || {});
      if (!(options.body instanceof FormData) && !headers.has("Content-Type")) {
        headers.set("Content-Type", "application/json");
      }
      headers.set("Authorization", `Bearer ${localStorage.getItem("access")}`);

      let response = await fetch(url, {
        ...options,
        headers,
      });

      if (response.status !== 401) return response;

      const nextAccessToken = await refreshAccessToken();
      if (!nextAccessToken) return null;

      const retryHeaders = new Headers(options.headers || {});
      if (
        !(options.body instanceof FormData) &&
        !retryHeaders.has("Content-Type")
      ) {
        retryHeaders.set("Content-Type", "application/json");
      }
      retryHeaders.set(
        "Authorization",
        `Bearer ${localStorage.getItem("access")}`,
      );

      response = await fetch(url, {
        ...options,
        headers: retryHeaders,
      });

      return response;
    },
    [refreshAccessToken],
  );

  const authUpload = useCallback(
    async (url, { method = "POST", body, onProgress } = {}) => {
      const sendRequest = (accessToken) => {
        return new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open(method, url);
          xhr.responseType = "text";
          xhr.setRequestHeader("Authorization", `Bearer ${accessToken}`);

          if (xhr.upload) {
            xhr.upload.onprogress = (event) => {
              if (!event.lengthComputable) return;
              if (typeof onProgress === "function") {
                onProgress(Math.round((event.loaded / event.total) * 100));
              }
            };
          }

          xhr.onload = () => {
            let payload = null;

            if (
              typeof xhr.responseText === "string" &&
              xhr.responseText.trim()
            ) {
              try {
                payload = JSON.parse(xhr.responseText);
              } catch (error) {
                payload = xhr.responseText;
              }
            }

            resolve({
              ok: xhr.status >= 200 && xhr.status < 300,
              status: xhr.status,
              payload,
            });
          };

          xhr.onerror = () => reject(new Error("upload-request-failed"));
          xhr.send(body);
        });
      };

      const currentAccessToken = localStorage.getItem("access");
      let response = await sendRequest(currentAccessToken);

      if (response.status !== 401) return response;

      const nextAccessToken = await refreshAccessToken();
      if (!nextAccessToken) return null;

      response = await sendRequest(nextAccessToken);
      return response;
    },
    [refreshAccessToken],
  );

  const fetchTicketLists = useCallback(
    async ({ withLoading = true } = {}) => {
      if (withLoading) setListLoading(true);
      setListError("");

      try {
        const priorityResponse = await authFetch(
          `${API_BASE_URL}support/trader/tickets/priority/`,
          {
            method: "GET",
          },
        );

        if (!priorityResponse) return;

        if (!priorityResponse.ok) {
          throw new Error("load-ticket-lists-failed");
        }

        const priorityPayload = await priorityResponse.json();

        const sortedPriorityTickets = sortByUpdatedAtDesc(
          Array.isArray(priorityPayload) ? priorityPayload : [],
        );

        setPriorityTickets(sortedPriorityTickets);
        return sortedPriorityTickets;
      } catch (error) {
        console.error("Error fetching support ticket lists:", error);
        setListError("بارگذاری لیست تیکت‌ها با خطا مواجه شد.");
        return [];
      } finally {
        if (withLoading) setListLoading(false);
      }
    },
    [authFetch],
  );

  const fetchTicketDetail = useCallback(
    async (ticketUuid) => {
      if (!ticketUuid) return;

      setDetailLoading(true);
      setDetailError("");
      try {
        const response = await authFetch(
          `${API_BASE_URL}support/tickets/${ticketUuid}/`,
          {
            method: "GET",
          },
        );

        if (!response) return;
        if (!response.ok) {
          throw new Error("load-ticket-detail-failed");
        }

        const payload = await response.json();
        const sortedChats = sortByCreatedAtAsc(
          Array.isArray(payload?.chats) ? payload.chats : [],
        );

        setSelectedTicket({
          ...payload,
          chats: sortedChats,
        });
      } catch (error) {
        console.error("Error fetching support ticket detail:", error);
        setDetailError("بارگذاری جزئیات گفتگو با خطا مواجه شد.");
        setSelectedTicket(null);
      } finally {
        setDetailLoading(false);
        // setIsTicketRefreshSpinning(false);
      }
    },
    [authFetch],
  );

  const markTicketAsRead = useCallback(
    async (ticketUuid) => {
      if (!ticketUuid) return;
      if (markReadInProgressRef.current.has(ticketUuid)) return;

      markReadInProgressRef.current.add(ticketUuid);
      try {
        const response = await authFetch(
          `${API_BASE_URL}support/tickets/${ticketUuid}/mark-read/`,
          {
            method: "POST",
          },
        );

        if (!response || !response.ok) return;
        await fetchTicketLists({ withLoading: false });
        await refetchNotificationCounts();
      } catch (error) {
        console.error("Error marking support ticket as read:", error);
      } finally {
        markReadInProgressRef.current.delete(ticketUuid);
      }
    },
    [authFetch, fetchTicketLists, refetchNotificationCounts],
  );

  const fetchAccounts = useCallback(async () => {
    setAccountsLoading(true);
    setAccountsError("");
    try {
      const response = await authFetch(
        `${API_BASE_URL}challenges/trader/accounts`,
        {
          method: "GET",
        },
      );

      if (!response) return;
      if (!response.ok) {
        throw new Error("load-accounts-failed");
      }

      const payload = await response.json();
      setAccounts(flattenAccounts(payload));
    } catch (error) {
      console.error("Error fetching accounts for support ticket:", error);
      setAccountsError("بارگذاری حساب‌ها با خطا مواجه شد.");
    } finally {
      setAccountsLoading(false);
    }
  }, [authFetch]);

  useEffect(() => {
    fetchTicketLists();
  }, [fetchTicketLists]);

  const activeTickets = priorityTickets;

  const allTicketsMap = useMemo(() => {
    return new Map(
      priorityTickets.map((ticket) => [ticket.ticket_uuid, ticket]),
    );
  }, [priorityTickets]);

  // useEffect(() => {
  //   if (listLoading) return;
  //   if (activeTickets.length === 0) {
  //     setSelectedTicketUuid(null);
  //     return;
  //   }

  //   const isSelectedInCurrentTab = activeTickets.some(
  //     (ticket) => ticket.ticket_uuid === selectedTicketUuid,
  //   );

  //   if (!isSelectedInCurrentTab) {
  //     setSelectedTicketUuid(activeTickets[0].ticket_uuid);
  //   }
  // }, [activeTickets, listLoading, selectedTicketUuid]);

  useEffect(() => {
    if (!selectedTicketUuid) {
      pendingInitialChatScrollTicketRef.current = null;
      setSelectedTicket(null);
      setDetailError("");
      setChatText("");
      setChatDocuments([]);
      setChatUploadProgress(null);
      setChatError("");
      return;
    }

    setSelectedTicket(null);
    fetchTicketDetail(selectedTicketUuid);
  }, [fetchTicketDetail, selectedTicketUuid, ]);

  useLayoutEffect(() => {
    const loadedTicketUuid =
      selectedTicket?.ticket_uuid || selectedTicket?.uuid || null;

    if (
      !selectedTicketUuid ||
      detailLoading ||
      detailError ||
      !selectedTicket ||
      pendingInitialChatScrollTicketRef.current !== selectedTicketUuid ||
      loadedTicketUuid !== selectedTicketUuid
    ) {
      return;
    }

    let timeoutId = null;
    let secondFrameId = null;
    const firstFrameId = requestAnimationFrame(() => {
      scrollChatToBottom();

      secondFrameId = requestAnimationFrame(() => {
        scrollChatToBottom();
      });
    });

    timeoutId = window.setTimeout(() => {
      scrollChatToBottom();
      pendingInitialChatScrollTicketRef.current = null;
    }, 180);

    return () => {
      cancelAnimationFrame(firstFrameId);
      if (secondFrameId !== null) cancelAnimationFrame(secondFrameId);
      if (timeoutId !== null) window.clearTimeout(timeoutId);
    };
  }, [
    detailError,
    detailLoading,
    scrollChatToBottom,
    selectedTicket,
    selectedTicketUuid,
  ]);

  useEffect(() => {
    if (!selectedTicketUuid) return;
    const selectedFromList = allTicketsMap.get(selectedTicketUuid);
    if (!selectedFromList) return;
    if ((selectedFromList?.unread_count_for_me || 0) <= 0) return;

    markTicketAsRead(selectedTicketUuid);
  }, [selectedTicketUuid, allTicketsMap, markTicketAsRead]);

  useEffect(() => {
    resizeChatTextarea(chatTextareaRef.current);
  }, [chatText, resizeChatTextarea]);

  const openCreateModal = () => {
    setIsCreateModalOpen(true);
    if (accounts.length === 0 && !accountsLoading) {
      fetchAccounts();
    }
  };

  const handleSelectTicket = (ticketUuid) => {
    if (!ticketUuid || ticketUuid === selectedTicketUuid) return;
    pendingInitialChatScrollTicketRef.current = ticketUuid;
    setSelectedTicket(null);
    setDetailError("");
    setSelectedTicketUuid(ticketUuid);
  };

  const handleCloseActiveChat = () => {
    setSelectedTicketUuid(null);
  };

  const handleRefreshTicketDetail = useCallback(() => {
    if (!selectedTicketUuid || detailLoading) return;

    pendingInitialChatScrollTicketRef.current = selectedTicketUuid;
    setSelectedTicket(null);
    setDetailError("");
    setTicketDetailRefreshSpinTick((prev) => prev + 1);
    void fetchTicketDetail(selectedTicketUuid);
  }, [detailLoading, fetchTicketDetail, selectedTicketUuid]);

  const handleRefreshTicketList = useCallback(() => {
    if (listLoading) return;

    setTicketListRefreshSpinTick((prev) => prev + 1);
    void fetchTicketLists({ withLoading: false });
  }, [fetchTicketLists, listLoading]);

  const closeCreateModal = () => {
    setIsCreateModalOpen(false);
    setCreateTitle("");
    setCreateDescription("");
    setCreateAccountCode("");
    setCreateDocuments([]);
    setCreateFormError("");
    setCreateUploadProgress(null);
    setCreateSubmitting(false);
  };

  const appendImagesToCreateDocs = (files = []) => {
    const incoming = Array.from(files);
    if (incoming.length === 0) return;
    setCreateFormError("");

    const imageFiles = incoming.filter((file) =>
      String(file?.type || "").startsWith("image/"),
    );

    if (imageFiles.length !== incoming.length) {
      setCreateFormError("فقط فایل تصویری مجاز است.");
    }

    if (imageFiles.length > 0) {
      setCreateDocuments((prev) => [...prev, ...imageFiles]);
    }
  };

  const appendImagesToChatDocs = (files = []) => {
    const incoming = Array.from(files);
    if (incoming.length === 0) return;
    setChatError("");

    const imageFiles = incoming.filter((file) =>
      String(file?.type || "").startsWith("image/"),
    );

    if (imageFiles.length !== incoming.length) {
      setChatError("فقط فایل تصویری مجاز است.");
    }

    if (imageFiles.length > 0) {
      setChatDocuments((prev) => [...prev, ...imageFiles]);
    }
  };

  const handleCreateTicket = async (event) => {
    if (event?.preventDefault) event.preventDefault();

    const title = createTitle.trim();
    const description = createDescription.trim();
    if (!title || !description) {
      setCreateFormError("عنوان و توضیحات الزامی است.");
      return;
    }

    setCreateSubmitting(true);
    setCreateUploadProgress(0);
    setCreateFormError("");
    try {
      const formData = new FormData();
      formData.append("title", title);
      formData.append("description", description);
      if (createAccountCode) {
        formData.append("account_code", createAccountCode);
      }
      const compressedDocuments = await compressImageFiles(createDocuments);
      compressedDocuments.forEach((file) => {
        formData.append("documents", file);
      });

      const response = await authUpload(
        `${API_BASE_URL}support/trader/tickets/`,
        {
          method: "POST",
          body: formData,
          onProgress: setCreateUploadProgress,
        },
      );

      if (!response) return;
      if (!response.ok) {
        setCreateFormError(
          getErrorMessageFromPayload(
            response.payload,
            "ایجاد تیکت با خطا مواجه شد.",
          ),
        );
        return;
      }

      setCreateUploadProgress(100);
      const refreshedTickets = await fetchTicketLists({ withLoading: false });
      const createdTicketUuid =
        response.payload?.ticket_uuid || response.payload?.uuid;
      const fallbackTicketUuid = refreshedTickets?.[0]?.ticket_uuid;

      closeCreateModal();
      setSelectedTicketUuid(createdTicketUuid || fallbackTicketUuid || null);
    } catch (error) {
      console.error("Error creating support ticket:", error);
      setCreateFormError("ایجاد تیکت با خطا مواجه شد.");
    } finally {
      setCreateUploadProgress(null);
      setCreateSubmitting(false);
    }
  };

  const handleSendChat = async (event) => {
    if (event?.preventDefault) event.preventDefault();
    if (!selectedTicketUuid) return;

    const ticketUuid = selectedTicketUuid;
    const text = chatText.trim();
    if (!text && chatDocuments.length === 0) {
      setChatError("");
      return;
    }

    setChatSubmitting(true);
    setChatUploadProgress(0);
    setChatError("");
    try {
      const formData = new FormData();
      if (text) formData.append("text", text);
      const compressedDocuments = await compressImageFiles(chatDocuments);
      compressedDocuments.forEach((file) => {
        formData.append("documents", file);
      });

      const response = await authUpload(
        `${API_BASE_URL}support/tickets/${ticketUuid}/chats/`,
        {
          method: "POST",
          body: formData,
          onProgress: setChatUploadProgress,
        },
      );

      if (!response) return;
      if (!response.ok) {
        setChatError(
          getErrorMessageFromPayload(
            response.payload,
            "ارسال پیام با خطا مواجه شد.",
          ),
        );
        return;
      }

      setChatUploadProgress(100);
      setChatText("");
      setChatDocuments([]);
      resetChatTextareaHeight();
      pendingInitialChatScrollTicketRef.current = ticketUuid;
      setSelectedTicketUuid(ticketUuid);

      const createdChat = response.payload?.chat;
      const responseTicketUuid = response.payload?.ticket_uuid || ticketUuid;
      if (createdChat) {
        setSelectedTicket((prevTicket) => {
          if (!prevTicket) return prevTicket;

          const existingChats = Array.isArray(prevTicket.chats)
            ? prevTicket.chats
            : [];
          const nextChatsById = new Map(
            existingChats.map((chat) => [chat.id, chat]),
          );
          nextChatsById.set(createdChat.id, createdChat);

          return {
            ...prevTicket,
            last_chat: createdChat,
            unread_count_for_me: 0,
            updated_at: createdChat.updated_at || createdChat.created_at,
            chats: sortByCreatedAtAsc(Array.from(nextChatsById.values())),
          };
        });

        setPriorityTickets((prevTickets) => {
          const nextTickets = prevTickets.map((ticket) =>
            ticket.ticket_uuid === responseTicketUuid
              ? {
                  ...ticket,
                  last_chat: createdChat,
                  unread_count_for_me: 0,
                  updated_at: createdChat.updated_at || createdChat.created_at,
                }
              : ticket,
          );

          if (
            !nextTickets.some(
              (ticket) => ticket.ticket_uuid === responseTicketUuid,
            )
          ) {
            nextTickets.push({
              ticket_uuid: responseTicketUuid,
              last_chat: createdChat,
              unread_count_for_me: 0,
              updated_at: createdChat.updated_at || createdChat.created_at,
            });
          }

          return sortByUpdatedAtDesc(nextTickets);
        });
      } else {
        await fetchTicketDetail(ticketUuid);
        await fetchTicketLists({ withLoading: false });
      }
    } catch (error) {
      console.error("Error sending support chat message:", error);
      setChatError("ارسال پیام با خطا مواجه شد.");
    } finally {
      setChatUploadProgress(null);
      setChatSubmitting(false);
    }
  };

  const handleChatKeyDown = (event) => {
    if (event.key !== "Enter" || event.shiftKey) return;
    if (event.nativeEvent?.isComposing) return;

    event.preventDefault();
    if (chatSubmitting) return;
    handleSendChat();
  };

  return (
    <LayoutPage
      activeIndex={14}
      navItems={["پشتیبانی"]}
      childrenClassName="flex-1 min-h-0 flex flex-col gap-2.5"
    >
      <div
        dir="rtl"
        className="w-full flex-1 min-h-0 grid grid-cols-1 min-[769px]:grid-cols-4 gap-x-4 gap-y-4"
      >
        <div
          className={`col-span-full min-[769px]:col-span-1 min-h-0 flex-col gap-2.5 ${
            selectedTicketUuid ? "hidden min-[769px]:flex" : "flex"
          }`}
        >
          <button
            type="button"
            onClick={openCreateModal}
            className="w-full flex items-center justify-center gap-1.5 rounded-lg py-2.5 text-base text-white bg-zi-gradient-button-blue hocus:ring-1 hocus:ring-zi-text-dark hocus:ring-inset hocus:ring-offset-1 hocus:ring-offset-zi-blue-light"
          >
            <SVG name="add" classname="size-2.5" />
            <span>ایجاد تیکت جدید</span>
          </button>

          <div className="w-full flex-1 min-h-0 overflow-y-auto divide-y divide-dashed divide-zi-border-dark [&>*:nth-child(2)]:!border-t-0 flex flex-col gap-2.5 rounded-2xl bg-zi-bg2-light dark:bg-zi-bg2-dark p-2.5 pt-0">
            {listLoading && <LoadingState />}

            {!listLoading && listError && (
              <p className="text-red-500 text-sm">{listError}</p>
            )}

            {!listLoading && !listError && activeTickets.length === 0 && (
              <EmptyState />
            )}
            {
              !listLoading &&
              !listError && 
              activeTickets.length !== 0 &&
              <div className="w-full flex justify-end items-center pt-2.5">
                <button onClick={handleRefreshTicketList} className="flex items-center gap-1 rounded-full bg-zi-bg1-light dark:bg-zi-bg1-dark px-2 py-1">
                  <span className="text-zi-text-light dark:text-zi-text-dark text-xs font-thin">بروزرسانی</span>
                  <motion.svg
                      key={ticketListRefreshSpinTick}
                      className="size-3 stroke-zi-text-light dark:stroke-zi-text-dark cursor-pointer"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      style={{ originX: "50%", originY: "50%" }}
                      initial={{ rotate: 0 }}
                      animate={
                        ticketListRefreshSpinTick > 0 ? { rotate: 360 } : { rotate: 0 }
                      }
                      transition={{ duration: 0.6, ease: "easeInOut" }}
                    >
                      <path
                        d="M21 3V8M21 8H16M21 8L18 5.29168C16.4077 3.86656 14.3051 3 12 3C7.02944 3 3 7.02944 3 12C3 16.9706 7.02944 21 12 21C16.2832 21 19.8675 18.008 20.777 14"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </motion.svg>
                  </button>
              </div>
            }
            {!listLoading &&
              !listError && 
              activeTickets.map((ticket) => {
                const isActive = ticket?.ticket_uuid === selectedTicketUuid;
                return (
                  <div key={ticket?.ticket_uuid} className="pt-2.5">
                    <button
                      type="button"
                      onClick={() => handleSelectTicket(ticket?.ticket_uuid)}
                      className={`w-full py-2.5 px-2.5 rounded-lg flex flex-col gap-2.5 text-right ${
                        isActive
                          ? " bg-zi-bg1-light dark:bg-zi-bg1-dark"
                          : "hocus:bg-zi-bg1-light dark:hocus:bg-zi-bg1-dark"
                      }`}
                    >
                      <div className="w-full flex items-center justify-between gap-2">
                        <p className="text-sm font-medium text-zi-text-light dark:text-zi-text-dark truncate">
                          {ticket?.title || "-"}
                        </p>
                        {ticket?.unread_count_for_me > 0 && (
                          <span className="px-[5px] rounded-full bg-zi-radial-gradient-blue text-[10px] text-zi-text-dark flex justify-center items-center">
                            {ticket?.unread_count_for_me}
                          </span>
                        )}
                      </div>

                      <div className="w-full flex items-center justify-between gap-2">
                        <p className="text-xs text-zi-text-light/60 dark:text-zi-text-dark/60 truncate">
                          {ticket?.last_chat?.text || "بدون پیام"}
                        </p>
                        <p className="text-xs font-thin text-zi-text-light dark:text-zi-text-dark whitespace-nowrap">{formatRelativeTimeAgo(ticket?.last_chat?.created_at)}</p>
                      </div>
                    </button>
                  </div>
                );
              })}
          </div>
        </div>
        <div
          className={`col-span-full min-[769px]:col-span-3 min-h-0 rounded-2xl bg-zi-bg2-light dark:bg-zi-bg2-dark text-right ${
            selectedTicketUuid ? "block" : "hidden min-[769px]:block"
          }`}
        >
          <div className="relative w-full h-full min-h-0">
            {selectedTicketUuid ? (
              <>
                <div className="absolute top-0 backdrop-blur-sm rounded-t-2xl bg-zi-bg2-light/70 dark:bg-zi-bg2-dark/70 w-full border-b border-zi-border-light dark:border-zi-border-dark p-2.5 flex items-center justify-between">
                  <div className="flex items-center justify-start gap-2.5">
                    <button
                      type="button"
                      onClick={handleCloseActiveChat}
                      aria-label="بازگشت به لیست گفتگوها"
                      className="cursor-pointer"
                    >
                      <SVG
                        name="arrowLong"
                        classname="w-5 stroke-zi-text-light dark:stroke-zi-text-dark"
                      />
                    </button>
                    <p className="font-medium text-zi-text-light dark:text-zi-text-dark w-full overflow-auto">
                      {selectedTicket?.title ||
                        allTicketsMap.get(selectedTicketUuid)?.title ||
                        ""}
                    </p>
                  </div>
                  <div>
                    <motion.svg
                    key={ticketDetailRefreshSpinTick}
                    className="size-4 stroke-zi-text-light dark:stroke-zi-text-dark cursor-pointer"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    style={{ originX: "50%", originY: "50%" }}
                    initial={{ rotate: 0 }}
                    animate={
                      ticketDetailRefreshSpinTick > 0 ? { rotate: 360 } : { rotate: 0 }
                    }
                    transition={{ duration: 0.6, ease: "easeInOut" }}
                    onClick={handleRefreshTicketDetail}
                  >
                    <path
                      d="M21 3V8M21 8H16M21 8L18 5.29168C16.4077 3.86656 14.3051 3 12 3C7.02944 3 3 7.02944 3 12C3 16.9706 7.02944 21 12 21C16.2832 21 19.8675 18.008 20.777 14"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </motion.svg>
                  </div>
                </div>

                <div
                  ref={chatScrollContainerRef}
                  className="h-full w-full pt-14 pb-20 px-7 min-h-0 overflow-y-auto flex flex-col gap-7"
                >
                  {detailLoading && <LoadingState />}

                  {!detailLoading && detailError && (
                    <p className="text-red-500 text-sm">{detailError}</p>
                  )}

                  {!detailLoading &&
                    !detailError &&
                    selectedTicket &&
                    selectedTicket.chats.length === 0 && <EmptyState />}

                  {!detailLoading &&
                    !detailError &&
                    selectedTicket &&
                    selectedTicket.chats.map((chat) => {
                      const isTrader = chat?.sender_role === "trader";

                      return (
                        <div
                          key={chat?.id}
                          className={`max-w-[85%] md:max-w-[70%] rounded-2xl px-4 py-3 flex flex-col gap-2 ${
                            isTrader
                              ? "ml-auto bg-zi-blue-light/15 dark:bg-zi-blue-light rounded-br-none text-zi-text-light dark:text-zi-text-dark"
                              : "mr-auto bg-zi-bg1-light dark:bg-zi-bg1-light/15 rounded-bl-none text-black dark:text-zi-text-dark"
                          }`}
                        >
                          {Array.isArray(chat?.documents) &&
                            chat.documents.length > 0 && (
                              <div className="flex flex-wrap gap-2">
                                {chat.documents.map((doc) => (
                                  <a
                                    key={doc?.id}
                                    href={doc?.image}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="rounded-md overflow-hidden border border-zi-border-light dark:border-zi-border-dark bg-zi-bg1-light dark:bg-zi-bg1-dark p-1 inline-flex"
                                  >
                                    <img
                                      src={doc?.image}
                                      alt="support-document"
                                      onLoad={handleChatDocumentLoad}
                                      className="max-w-[220px] max-h-44 w-auto h-auto object-contain rounded"
                                    />
                                  </a>
                                ))}
                              </div>
                            )}
                          {/* <p className="text-xs text-[#4b5563] dark:text-[#b7cadb]">
                            {isTrader
                              ? chat?.sender_username || "شما"
                              : chat?.sender_username || "کارشناس پشتیبانی"}
                          </p> */}
                          {chat?.text && (
                              <p className="text-base break-words whitespace-pre-wrap leading-7">
                                {chat.text}
                              </p>
                          )}
                          <p className="text-xs font-thin text-zi-text-light/50 dark:text-zi-text-dark/50">{formatChatDateTime(chat?.created_at)}</p>
                        </div>
                      );
                    })}
                </div>

                <form
                  onSubmit={handleSendChat}
                  className="absolute bottom-0 rounded-b-2xl p-2.5 backdrop-blur-sm bg-zi-bg2-light/70 dark:bg-zi-bg2-dark/70 w-full flex flex-col gap-3"
                >
                  <ImageUploadPreviewGrid
                    files={chatDocuments}
                    uploading={chatSubmitting}
                    uploadProgress={chatUploadProgress ?? 0}
                    onRemove={(index) =>
                      setChatDocuments((prev) =>
                        prev.filter(
                          (_, currentIndex) => currentIndex !== index,
                        ),
                      )
                    }
                  />

                  <div className="w-full flex items-end gap-2.5">
                    <textarea
                      ref={chatTextareaRef}
                      value={chatText}
                      disabled={chatSubmitting}
                      rows={1}
                      enterKeyHint="send"
                      onChange={(event) => {
                        setChatText(event.target.value);
                        resizeChatTextarea(event.target);
                        if (chatError) setChatError("");
                      }}
                      onKeyDown={handleChatKeyDown}
                      placeholder="پیام خود را اینجا بنویسید..."
                      className="flex-1 min-w-0 min-h-11 max-h-28 resize-none overflow-y-auto rounded-[22px] border border-zi-border-dark bg-zi-bg1-light/50 dark:bg-zi-bg1-dark/50 px-5 py-2.5 text-base leading-6 text-zi-text-light dark:text-zi-text-dark outline-none hocus:ring-1 hocus:ring-zi-border-dark"
                    />

                    <label
                      htmlFor="support-chat-upload"
                      className={` h-11 aspect-square rounded-full border border-zi-border-dark flex items-center justify-center bg-zi-bg1-light/50 dark:bg-zi-bg1-dark/50  ${
                        chatSubmitting
                          ? "cursor-not-allowed opacity-60 pointer-events-none"
                          : "cursor-pointer hocus:ring-1 hocus:ring-zi-border-dark"
                      }`}
                    >
                      <SVG
                        name="upload"
                        classname="size-5 stroke-zi-text-light dark:stroke-zi-text-dark"
                      />
                    </label>
                    <input
                      id="support-chat-upload"
                      type="file"
                      accept={KYC_FILE_ACCEPT}
                      multiple
                      className="hidden"
                      disabled={chatSubmitting}
                      onChange={(event) => {
                        appendImagesToChatDocs(event.target.files);
                        event.target.value = "";
                      }}
                    />

                    <button
                      type="submit"
                      disabled={chatSubmitting}
                      className={`h-11 aspect-square rounded-full flex items-center justify-center text-sm text-white bg-zi-gradient-button-blue disabled:opacity-50 hocus:ring-1 hocus:ring-zi-border-dark ${!chatText.trim() && chatDocuments.length === 0 ? "opacity-50 cursor-not-allowed" : ""}`}
                    >
                      {chatSubmitting ? (
                        <Spinner color="white" size={24} stroke={4} />
                      ) : (
                        <SVG name="send" classname="size-6" />
                      )}
                    </button>
                  </div>

                  {chatError && (
                    <p className="text-red-500 text-sm">{chatError}</p>
                  )}
                </form>
              </>
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center gap-5">
                <p className="text-zi-text-light dark:text-zi-text-dark">
                  یک گفتگو را باز کنید یا تیکت جدید ایجاد کنید
                </p>
                <SVG name="noChat" classname="w-32 h-fit" />
              </div>
            )}
          </div>
        </div>
      </div>

      {isCreateModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
          onClick={closeCreateModal}
        >
          <div
            dir="rtl"
            className="w-11/12 max-w-2xl max-h-[70dvh] overflow-hidden rounded-2xl bg-zi-bg2-light dark:bg-zi-bg2-dark p-5 sm:p-7 flex flex-col gap-5"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="w-full flex items-start justify-between gap-3 mb-5">
              <h2 className="text-lg font-medium text-zi-text-light dark:text-zi-text-dark">
                ایجاد تیکت جدید
              </h2>
              <span onClick={() => setIsCreateModalOpen(false)}>
                <SVG
                  name="close"
                  classname="stroke-zi-text-light dark:stroke-zi-text-dark size-5 cursor-pointer"
                />
              </span>
            </div>

            <form
              onSubmit={handleCreateTicket}
              className="flex flex-col gap-4 overflow-hidden"
            >
              <div className="flex flex-col gap-4 overflow-y-auto h-full">
                <div className="flex flex-col gap-1.5">
                  <label className="text-sm text-zi-text-light/70 dark:text-zi-text-dark/70">
                    انتخاب حساب مرتبط با تیکت
                  </label>
                  {accountsLoading ? (
                    <LoadingState />
                  ) : (
                    <div className={`relative inline-flex items-center w-full`}>
                      <select
                        value={createAccountCode}
                        disabled={createSubmitting}
                        onChange={(event) =>
                          setCreateAccountCode(event.target.value)
                        }
                        className="w-full rounded-lg border border-zi-border-light dark:border-zi-border-dark bg-zi-bg1-light dark:bg-zi-bg1-dark px-3 py-2 text-sm text-zi-text-light dark:text-zi-text-dark outline-none appearance-none cursor-pointer"
                      >
                        <option value="">بدون انتخاب حساب</option>
                        {accounts.map((account) => (
                          <option
                            key={account?.id}
                            value={String(account?.code || "")}
                          >
                            {buildAccountLabel(account)}
                          </option>
                        ))}
                      </select>
                      <div className="pointer-events-none absolute left-3 flex items-center">
                        <SVG
                          name="select"
                          classname="size-3 stroke-zi-text-light dark:stroke-zi-text-dark"
                        />
                      </div>
                    </div>
                  )}
                  {accountsError && (
                    <p className="text-red-500 text-sm">{accountsError}</p>
                  )}
                </div>

                <div className="flex flex-col gap-1.5">
                  <label className="text-sm text-zi-text-light/70 dark:text-zi-text-dark/70">
                    عنوان
                  </label>
                  <input
                    type="text"
                    value={createTitle}
                    disabled={createSubmitting}
                    onChange={(event) => {
                      setCreateTitle(event.target.value);
                      if (createFormError) setCreateFormError("");
                    }}
                    placeholder="عنوان تیکت را اینجا بنویسید"
                    className="w-full rounded-lg border border-zi-border-light dark:border-zi-border-dark bg-zi-bg1-light dark:bg-zi-bg1-dark px-3 py-2 text-sm text-zi-text-light dark:text-zi-text-dark outline-none hocus:ring-1 ring-inset hocus:ring-zi-border-light dark:hocus:ring-zi-border-dark"
                  />
                </div>

                <div className="flex flex-col gap-1.5">
                  <label className="text-sm text-zi-text-light/70 dark:text-zi-text-dark/70">
                    توضیحات
                  </label>
                  <textarea
                    value={createDescription}
                    disabled={createSubmitting}
                    onChange={(event) => {
                      setCreateDescription(event.target.value);
                      if (createFormError) setCreateFormError("");
                    }}
                    placeholder="توضیحات تیکت را اینجا بنویسید"
                    className="w-full min-h-28 resize-none rounded-lg border border-zi-border-light dark:border-zi-border-dark bg-zi-bg1-light dark:bg-zi-bg1-dark px-3 py-2 text-sm text-zi-text-light dark:text-zi-text-dark outline-none hocus:ring-1 ring-inset hocus:ring-zi-border-light dark:hocus:ring-zi-border-dark"
                  />
                </div>

                <div className="flex flex-col gap-1.5">
                  <label className="text-sm text-zi-text-light/70 dark:text-zi-text-dark/70">
                    مستندات
                  </label>
                  <label
                    htmlFor="support-create-upload"
                    className={`w-full h-20 rounded-lg border border-zi-border-light dark:border-zi-border-dark bg-zi-bg1-light dark:bg-zi-bg1-dark p-3 flex items-center justify-between gap-3 ${
                      createSubmitting
                        ? "cursor-not-allowed opacity-60 pointer-events-none"
                        : "cursor-pointer"
                    }`}
                  >
                    {createDocuments.length > 0 ? (
                      <ImageUploadPreviewGrid
                        files={createDocuments}
                        uploading={createSubmitting}
                        uploadProgress={createUploadProgress ?? 0}
                        onRemove={(index) =>
                          setCreateDocuments((prev) =>
                            prev.filter(
                              (_, currentIndex) => currentIndex !== index,
                            ),
                          )
                        }
                        disabled={createSubmitting}
                        wrap={false}
                        showFileName={false}
                        cardClassName="h-full aspect-square shrink-0"
                        previewClassName="size-full"
                        className="min-w-0 flex-1 h-full overflow-x-auto overflow-y-hidden"
                      />
                    ) : (
                      <p className="min-w-0 flex-1 text-sm text-zi-text-light/70 dark:text-zi-text-dark/70">
                        مستندات را انتخاب کنید
                      </p>
                    )}
                    <SVG
                      name="upload"
                      classname="size-5 shrink-0 stroke-zi-text-light dark:stroke-zi-text-dark"
                    />
                  </label>
                  <input
                    id="support-create-upload"
                    type="file"
                    accept={KYC_FILE_ACCEPT}
                    multiple
                    className="hidden"
                    disabled={createSubmitting}
                    onChange={(event) => {
                      appendImagesToCreateDocs(event.target.files);
                      event.target.value = "";
                    }}
                  />
                </div>
              </div>

              {createFormError && (
                <p className="text-red-500 text-sm">{createFormError}</p>
              )}

              <button
                type="submit"
                disabled={createSubmitting}
                className={`w-full rounded-lg py-2.5 text-white bg-zi-gradient-button-blue disabled:opacity-50`}
              >
                {createSubmitting ? "در حال ایجاد..." : "ایجاد تیکت"}
              </button>
            </form>
          </div>
        </div>
      )}
    </LayoutPage>
  );
}
